const asObject = value =>
	value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const asArray = value => (Array.isArray(value) ? value : []);

const trimmed = value =>
	typeof value === 'string' || typeof value === 'number'
		? String(value).trim()
		: '';

const getDiagnostics = order => asObject(order?.importDiagnostics);
const getRowId = order =>
	order?.__importRowId || getDiagnostics(order).clientRowId;

const uniqueStrings = values => {
	const seen = new Set();
	return asArray(values).reduce((result, value) => {
		const normalized = trimmed(value);
		if (!normalized || seen.has(normalized)) return result;
		seen.add(normalized);
		result.push(normalized);
		return result;
	}, []);
};

const uniqueAliases = values => {
	const seen = new Set();
	return asArray(values)
		.reduce((result, value) => {
			const alias = trimmed(value);
			const normalized = alias.toLowerCase();
			if (!alias || seen.has(normalized)) return result;
			seen.add(normalized);
			result.push(alias);
			return result;
		}, [])
		.sort((left, right) =>
			left.toLowerCase().localeCompare(right.toLowerCase()),
		);
};

const getPositiveInteger = value => {
	const number = Number(value);
	return Number.isInteger(number) && number > 0 ? number : undefined;
};

const POSTGRES_INT4_MAX = 2147483647;
const getPositiveInt4 = value => {
	const number = Number(value);
	return Number.isSafeInteger(number) &&
		number > 0 &&
		number <= POSTGRES_INT4_MAX
		? number
		: undefined;
};

const getTargetRowIds = value =>
	uniqueStrings(asObject(value).targetRowIds).sort();

export const CONTRACT_FREQUENCY_INTERVAL_UNITS = [
	'day',
	'week',
	'month',
	'year',
];

const getSourceValue = order => {
	const diagnostics = getDiagnostics(order);
	const creation = asObject(diagnostics.frequencyCreation);
	const source = asObject(creation.source);
	if (Object.prototype.hasOwnProperty.call(source, 'value'))
		return trimmed(source.value);
	return trimmed(asObject(diagnostics.frequencyMatch).source?.value);
};

export const normalizeContractFrequencyCode = value =>
	trimmed(value)
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.toUpperCase()
		.replace(/[^A-Z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');

export const getContractFrequencyCreationPresentation = order => {
	const diagnostics = getDiagnostics(order);
	const creation = asObject(diagnostics.frequencyCreation);
	const status = trimmed(creation.status).toLowerCase();
	const cohortKey = trimmed(creation.cohortKey);
	const sourceValue = getSourceValue(order);
	const targetRowIds = getTargetRowIds(creation);
	return {
		status,
		reason: trimmed(creation.reason).toLowerCase(),
		cohortKey,
		allowed: creation.allowed === true,
		canCreate: creation.canCreate === true,
		targetRowIds,
		affectedCount: getPositiveInteger(creation.affectedCount),
		sourceValue,
		normalizedSourceValue: trimmed(asObject(creation.source).normalizedValue),
		canRequest:
			creation.allowed === true &&
			creation.canCreate === true &&
			['available', 'invalid'].includes(status) &&
			diagnostics.needsRevalidation !== true &&
			Boolean(cohortKey) &&
			Boolean(sourceValue) &&
			targetRowIds.length > 0,
	};
};

export const getContractFrequencyCreationDraft = (order, artifact) => {
	const sourceAlias = getSourceValue(order);
	const descriptor = asObject(
		artifact?.descriptor ||
			artifact?.request?.descriptor ||
			asObject(getDiagnostics(order).frequencyCreation).plan?.descriptor,
	);
	return {
		name: trimmed(descriptor.name) || sourceAlias,
		code:
			trimmed(descriptor.code) || normalizeContractFrequencyCode(sourceAlias),
		intervalCount:
			descriptor.intervalCount === undefined ||
			descriptor.intervalCount === null
				? ''
				: descriptor.intervalCount,
		intervalUnit: trimmed(descriptor.intervalUnit).toLowerCase(),
		sourceAlias,
	};
};

export const getContractFrequencyCreationCohortTargets = (
	orders,
	sourceOrder,
) => {
	const source = getContractFrequencyCreationPresentation(sourceOrder);
	if (!source.cohortKey || !source.targetRowIds.length) return [];
	const targetRowIds = new Set(source.targetRowIds);
	return asArray(orders).filter(order => {
		const presentation = getContractFrequencyCreationPresentation(order);
		return (
			targetRowIds.has(String(getRowId(order))) &&
			presentation.cohortKey === source.cohortKey &&
			Boolean(getRowId(order)) &&
			['available', 'invalid', 'ready', 'selected_pending_anchor'].includes(
				presentation.status,
			)
		);
	});
};

const normalizeDescriptor = value => {
	const descriptor = asObject(value);
	const code = trimmed(descriptor.code);
	const name = trimmed(descriptor.name);
	const intervalCount = getPositiveInt4(descriptor.intervalCount);
	const intervalUnit = trimmed(descriptor.intervalUnit).toLowerCase();
	const aliases = uniqueAliases(descriptor.aliases);
	if (
		!code ||
		code.length > 32 ||
		!/^[A-Z][A-Z0-9_]*$/.test(code) ||
		!name ||
		name.length > 80 ||
		!intervalCount ||
		!CONTRACT_FREQUENCY_INTERVAL_UNITS.includes(intervalUnit) ||
		!aliases.length ||
		aliases.length > 20 ||
		aliases.some(alias => alias.length > 80)
	)
		return;
	return { code, name, intervalCount, intervalUnit, aliases };
};

export const getContractFrequencyCreationDraftErrors = ({
	orders,
	sourceOrder,
	draft,
}) => {
	const errors = {};
	const name = trimmed(draft?.name);
	const code = trimmed(draft?.code);
	const intervalCount = Number(draft?.intervalCount);
	const intervalUnit = trimmed(draft?.intervalUnit).toLowerCase();
	if (!name) errors.name = 'Escribe el nombre de la nueva frecuencia.';
	else if (name.length > 80)
		errors.name = 'El nombre no puede exceder 80 caracteres.';
	if (!code) errors.code = 'Escribe un código válido para la frecuencia.';
	else if (code.length > 32)
		errors.code = 'El código no puede exceder 32 caracteres.';
	else if (!/^[A-Z][A-Z0-9_]*$/.test(code))
		errors.code =
			'El código debe iniciar con una letra mayúscula y usar solo A-Z, 0-9 o guion bajo.';
	if (!Number.isInteger(intervalCount) || intervalCount <= 0)
		errors.intervalCount = 'Escribe un intervalo entero mayor que cero.';
	else if (
		!Number.isSafeInteger(intervalCount) ||
		intervalCount > POSTGRES_INT4_MAX
	)
		errors.intervalCount = 'El intervalo no puede exceder 2147483647.';
	if (!CONTRACT_FREQUENCY_INTERVAL_UNITS.includes(intervalUnit))
		errors.intervalUnit = 'Selecciona una unidad de frecuencia válida.';
	const aliases = uniqueAliases(
		getContractFrequencyCreationCohortTargets(orders, sourceOrder).map(
			getSourceValue,
		),
	);
	if (aliases.length > 20)
		errors.aliases = 'La cohorte no puede contener más de 20 aliases.';
	else if (aliases.some(alias => alias.length > 80))
		errors.aliases = 'Cada alias no puede exceder 80 caracteres.';
	return errors;
};

export const buildContractFrequencyCreationRequest = ({
	orders,
	sourceOrder,
	draft,
	baseArtifact,
}) => {
	const presentation = getContractFrequencyCreationPresentation(sourceOrder);
	const baseRequest = normalizeRequest(baseArtifact?.request || baseArtifact);
	const canReprepare =
		baseRequest?.cohortKey === presentation.cohortKey &&
		JSON.stringify(baseRequest?.targetRowIds) ===
			JSON.stringify(presentation.targetRowIds);
	if (!presentation.canRequest && !canReprepare)
		throw new Error(
			'La creación de frecuencia ya no está disponible. Valida nuevamente.',
		);
	const errors = getContractFrequencyCreationDraftErrors({
		orders,
		sourceOrder,
		draft,
	});
	const firstError = Object.values(errors)[0];
	if (firstError) throw new Error(firstError);
	const name = trimmed(draft?.name);
	const code = trimmed(draft?.code);
	const intervalCount = getPositiveInt4(draft?.intervalCount);
	const intervalUnit = trimmed(draft?.intervalUnit).toLowerCase();
	const targets = getContractFrequencyCreationCohortTargets(
		orders,
		sourceOrder,
	);
	const targetRowIds = targets.map(getRowId).map(String).sort();
	if (targetRowIds.length !== presentation.targetRowIds.length)
		throw new Error(
			'La cohorte de frecuencia cambió. Valida nuevamente antes de continuar.',
		);
	const aliases = uniqueAliases(targets.map(getSourceValue));
	if (!targetRowIds.length || !aliases.length)
		throw new Error('No hay filas válidas en la cohorte de esta frecuencia.');
	return {
		cohortKey: presentation.cohortKey,
		descriptor: { code, name, intervalCount, intervalUnit, aliases },
		targetRowIds,
	};
};

const normalizeRequest = value => {
	const request = asObject(value);
	const cohortKey = trimmed(request.cohortKey);
	const descriptor = normalizeDescriptor(request.descriptor);
	const targetRowIds = uniqueStrings(request.targetRowIds).sort();
	if (!cohortKey || !descriptor || !targetRowIds.length) return;
	return { cohortKey, descriptor, targetRowIds };
};

export const getContractFrequencyCreationRequestsPayload = requests => {
	const byCohort = new Map();
	asArray(requests).forEach(value => {
		const request = normalizeRequest(value?.request || value);
		if (request) byCohort.set(request.cohortKey, request);
	});
	return Array.from(byCohort.values()).sort((left, right) =>
		left.cohortKey.localeCompare(right.cohortKey),
	);
};

const normalizePlan = value => {
	const creation = asObject(value);
	const plan = asObject(creation.plan);
	const status = trimmed(creation.status).toLowerCase();
	const cohortKey = trimmed(creation.cohortKey);
	const descriptor = normalizeDescriptor(plan.descriptor);
	const targetRowIds = getTargetRowIds(creation);
	const challenge = trimmed(creation.challenge);
	const mode = trimmed(plan.mode).toLowerCase();
	const existingFrequencyId =
		plan.existingFrequencyId === null
			? null
			: getPositiveInteger(plan.existingFrequencyId);
	const expiresAt = Number(plan.expiresAt);
	const hasValidModeTarget =
		(mode === 'create' && existingFrequencyId === null) ||
		(mode === 'reuse' && Boolean(existingFrequencyId));
	if (
		!['ready', 'selected_pending_anchor'].includes(status) ||
		!cohortKey ||
		!descriptor ||
		!targetRowIds.length ||
		!challenge ||
		!hasValidModeTarget ||
		!Number.isFinite(expiresAt) ||
		expiresAt <= 0
	)
		return;
	return {
		status,
		cohortKey,
		descriptor,
		targetRowIds,
		challenge,
		mode,
		existingFrequencyId,
		expiresAt,
	};
};

export const getContractFrequencyCreationPlan = order => {
	const creation = asObject(getDiagnostics(order).frequencyCreation);
	return normalizePlan(creation);
};

export const getContractFrequencyCreationPlansFromOrders = orders => {
	const byChallenge = new Map();
	asArray(orders).forEach(order => {
		const plan = getContractFrequencyCreationPlan(order);
		if (plan && !byChallenge.has(plan.challenge))
			byChallenge.set(plan.challenge, plan);
	});
	return Array.from(byChallenge.values()).sort((left, right) =>
		left.cohortKey.localeCompare(right.cohortKey),
	);
};

export const getContractFrequencyCreationSelectionsPayload = selections => {
	const byChallenge = new Map();
	asArray(selections).forEach(selection => {
		const challenge = trimmed(selection?.challenge);
		const targetRowIds = uniqueStrings(selection?.targetRowIds).sort();
		if (challenge && targetRowIds.length && !byChallenge.has(challenge))
			byChallenge.set(challenge, { challenge, targetRowIds });
	});
	return Array.from(byChallenge.values()).sort((left, right) =>
		left.challenge.localeCompare(right.challenge),
	);
};

export const getContractFrequencyCreationPlanFingerprint = plans =>
	JSON.stringify(
		asArray(plans)
			.map(plan => ({
				cohortKey: trimmed(plan?.cohortKey),
				descriptor: normalizeDescriptor(plan?.descriptor) || null,
				targetRowIds: uniqueStrings(plan?.targetRowIds).sort(),
				mode: trimmed(plan?.mode),
				existingFrequencyId:
					plan?.existingFrequencyId === null
						? null
						: getPositiveInteger(plan?.existingFrequencyId) || null,
			}))
			.sort((left, right) => left.cohortKey.localeCompare(right.cohortKey)),
	);

export const isOrderTargetedByContractFrequencyCreation = (order, selection) =>
	Boolean(getRowId(order)) &&
	uniqueStrings(selection?.targetRowIds).includes(String(getRowId(order)));

const CREATION_ISSUES = new Set([
	'CONTRACT_FREQUENCY_UNKNOWN',
	'CONTRACT_FREQUENCY_REVIEW_REQUIRED',
	'CONTRACT_FREQUENCY_CREATION_APPROVAL_REQUIRED',
]);

const getSemanticCreationArtifact = artifact => {
	const value = asObject(artifact);
	const cohortKey = trimmed(value.cohortKey);
	const descriptor = normalizeDescriptor(value.descriptor);
	const targetRowIds = uniqueStrings(value.targetRowIds).sort();
	if (!cohortKey || !descriptor || !targetRowIds.length) return;
	return {
		cohortKey,
		descriptor: {
			...descriptor,
			aliases: descriptor.aliases.map(alias => alias.toLowerCase()).sort(),
		},
		targetRowIds,
	};
};

export const areContractFrequencyCreationArtifactsSemanticallyEqual = (
	left,
	right,
) => {
	const leftSemantics = getSemanticCreationArtifact(left);
	const rightSemantics = getSemanticCreationArtifact(right);
	return Boolean(
		leftSemantics &&
			rightSemantics &&
			JSON.stringify(leftSemantics) === JSON.stringify(rightSemantics),
	);
};

export const isContractFrequencyCreationIssueResolved = ({
	order,
	issue,
	selections,
}) => {
	if (issue?.code !== 'CONTRACT_FREQUENCY_UNKNOWN') return false;
	const renewedPlan = getContractFrequencyCreationPlan(order);
	const renewedSemantics = getSemanticCreationArtifact(renewedPlan);
	if (!renewedSemantics) return false;
	return asArray(selections).some(selection => {
		if (selection?.validated !== true) return false;
		if (!isOrderTargetedByContractFrequencyCreation(order, selection))
			return false;
		return areContractFrequencyCreationArtifactsSemanticallyEqual(
			selection,
			renewedPlan,
		);
	});
};

export const setContractFrequencyCreationSelected = ({
	order,
	selection,
	selected,
}) => {
	if (!isOrderTargetedByContractFrequencyCreation(order, selection))
		return order;
	const diagnostics = getDiagnostics(order);
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			issues: asArray(diagnostics.issues).map(issue =>
				CREATION_ISSUES.has(issue?.code)
					? { ...issue, blocking: selected !== true }
					: issue,
			),
		},
	};
};

export const invalidateContractFrequencyCreationCapabilities = order => {
	const diagnostics = getDiagnostics(order);
	const creation = asObject(diagnostics.frequencyCreation);
	if (!Object.keys(creation).length) return order;
	const available = {
		...(creation.allowed === true ? { allowed: true } : {}),
		...(creation.canCreate === true ? { canCreate: true } : {}),
		status: 'invalid',
		...(trimmed(creation.cohortKey)
			? { cohortKey: trimmed(creation.cohortKey) }
			: {}),
		...(Object.keys(asObject(creation.source)).length
			? { source: creation.source }
			: {}),
		...(getTargetRowIds(creation).length
			? { targetRowIds: getTargetRowIds(creation) }
			: {}),
		...(getPositiveInteger(creation.affectedCount)
			? { affectedCount: getPositiveInteger(creation.affectedCount) }
			: {}),
	};
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			frequencyCreation: available,
		},
	};
};

export const invalidateContractFrequencyCreationCohorts = ({
	orders,
	requests,
	selections,
	changedRowIds,
}) => {
	const changedIds = new Set(uniqueStrings(changedRowIds));
	const artifacts = [...asArray(requests), ...asArray(selections)].map(
		artifact => artifact?.request || artifact,
	);
	const affectedCohorts = new Set();
	asArray(orders).forEach(order => {
		const presentation = getContractFrequencyCreationPresentation(order);
		if (
			presentation.cohortKey &&
			(changedIds.has(String(getRowId(order))) ||
				presentation.targetRowIds.some(rowId => changedIds.has(String(rowId))))
		)
			affectedCohorts.add(presentation.cohortKey);
	});
	artifacts.forEach(artifact => {
		const cohortKey = trimmed(artifact?.cohortKey);
		if (
			cohortKey &&
			uniqueStrings(artifact?.targetRowIds).some(rowId =>
				changedIds.has(String(rowId)),
			)
		)
			affectedCohorts.add(cohortKey);
	});

	if (!affectedCohorts.size)
		return {
			orders: asArray(orders),
			requests: asArray(requests),
			selections: asArray(selections),
			cohortKeys: [],
		};

	const affectedTargetIds = new Set(changedIds);
	asArray(orders).forEach(order => {
		const presentation = getContractFrequencyCreationPresentation(order);
		if (!affectedCohorts.has(presentation.cohortKey)) return;
		const rowId = getRowId(order);
		if (rowId !== undefined && rowId !== null)
			affectedTargetIds.add(String(rowId));
		presentation.targetRowIds.forEach(targetId =>
			affectedTargetIds.add(String(targetId)),
		);
	});
	artifacts.forEach(artifact => {
		if (!affectedCohorts.has(trimmed(artifact?.cohortKey))) return;
		uniqueStrings(artifact?.targetRowIds).forEach(targetId =>
			affectedTargetIds.add(String(targetId)),
		);
	});

	return {
		orders: asArray(orders).map(order => {
			const presentation = getContractFrequencyCreationPresentation(order);
			const rowId = getRowId(order);
			if (
				!affectedCohorts.has(presentation.cohortKey) &&
				!affectedTargetIds.has(String(rowId))
			)
				return order;
			const invalidated =
				invalidateContractFrequencyCreationCapabilities(order);
			if (invalidated !== order) return invalidated;
			return {
				...order,
				importDiagnostics: {
					...getDiagnostics(order),
					needsRevalidation: true,
				},
			};
		}),
		requests: asArray(requests).filter(
			artifact =>
				!affectedCohorts.has(
					trimmed((artifact?.request || artifact)?.cohortKey),
				),
		),
		selections: asArray(selections).filter(
			artifact =>
				!affectedCohorts.has(
					trimmed((artifact?.request || artifact)?.cohortKey),
				),
		),
		cohortKeys: Array.from(affectedCohorts).sort(),
	};
};

export const getContractFrequencyCreationSummary = selections => {
	const uniquePlans = new Map();
	asArray(selections).forEach(selection => {
		if (trimmed(selection?.cohortKey))
			uniquePlans.set(trimmed(selection.cohortKey), selection);
	});
	const plans = Array.from(uniquePlans.values());
	return {
		frequencyCount: plans.length,
		affectedOrderCount: new Set(
			plans.flatMap(plan => uniqueStrings(plan.targetRowIds)),
		).size,
	};
};
